import React from "react";
import {
  Box,
  VStack,
  Text,
  Heading,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useColorMode,
  List,
  ListItem,
  Flex,
  Badge,
  useBreakpointValue,
} from "@chakra-ui/react";
import { ContentItem } from "../libs/types";
import { TFunction } from "i18next";

interface TableOfContentProps {
  isOpen: boolean;
  onClose: () => void;
  contentItems: ContentItem[];
  currentChapterIndex: number;
  onChapterSelect: (index: number) => void;
  isMobile: boolean | undefined;
  t?: TFunction;
}

const TableOfContent: React.FC<TableOfContentProps> = ({
  isOpen,
  onClose,
  contentItems,
  currentChapterIndex,
  onChapterSelect,
  isMobile,
  t,
}) => {
  const { colorMode } = useColorMode();
  const drawerSize = useBreakpointValue({ base: "full", md: "xs" });

  const handleChapterClick = (index: number) => {
    onChapterSelect(index);
    onClose();
  };

  return (
    <Drawer
      isOpen={isOpen}
      placement="left"
      onClose={onClose}
      size={drawerSize}
    >
      <DrawerOverlay bgColor="rgba(0,0,0,0.3)" />
      <DrawerContent
        bg={colorMode === "light" ? "white" : "#1A202C"}
        color={colorMode === "light" ? "gray.800" : "white"}
        boxShadow="xl"
        maxWidth={isMobile ? "100%" : "350px"}
      >
        <DrawerCloseButton size="lg" />
        <DrawerHeader borderBottomWidth="1px" py={4}>
          <Heading size="md">
            {t
              ? t("readBook.tableOfContents", "Table of Contents")
              : "Table of Contents"}
          </Heading>
          <Text
            fontSize="sm"
            mt={1}
            color={colorMode === "light" ? "gray.500" : "gray.400"}
          >
            {t
              ? t("readBook.chapterCount", "{{count}} Chapters", {
                  count: contentItems.length,
                })
              : `${contentItems.length} ${
                  contentItems.length === 1 ? "Chapter" : "Chapters"
                }`}
          </Text>
        </DrawerHeader>

        <DrawerBody p={0}>
          <List spacing={0}>
            {contentItems.map((item, index) => (
              <ListItem key={index}>
                <Box
                  onClick={() => handleChapterClick(index)}
                  cursor="pointer"
                  p={3}
                  py={4}
                  borderBottom="none"
                  bg={
                    currentChapterIndex === index
                      ? colorMode === "light"
                        ? "gray.100"
                        : "rgba(45, 55, 72, 0.3)"
                      : "transparent"
                  }
                  _hover={{
                    bg:
                      colorMode === "light"
                        ? "gray.100"
                        : "rgba(255, 255, 255, 0.08)",
                  }}
                  position="relative"
                  pl={4}
                  _before={{
                    content: '""',
                    position: "absolute",
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: "4px",
                    bg:
                      currentChapterIndex === index
                        ? colorMode === "light"
                          ? "gray.600"
                          : "gray.400"
                        : "transparent",
                  }}
                  transition="all 0.2s ease"
                >
                  <Flex align="center" justify="space-between">
                    <Flex align="center" maxWidth="85%">
                      <Text
                        fontSize="sm"
                        opacity={0.7}
                        mr={2}
                        minWidth="28px"
                        fontWeight="normal"
                      >
                        {index + 1}.
                      </Text>
                      <Text
                        noOfLines={1}
                        fontSize="md"
                        fontWeight={
                          currentChapterIndex === index ? "bold" : "normal"
                        }
                        width="calc(100% - 36px)"
                      >
                        {item.chapter}
                      </Text>
                    </Flex>
                  </Flex>
                </Box>
              </ListItem>
            ))}
          </List>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default TableOfContent;
